/**
 * Cookie-notice timing: when the first-layer prompt may appear.
 *
 * Counsel memo 002 §5.3. Pure: no document, window, Date.now, or storage.
 * The banner reads cookies and storage in effects and passes the numbers here.
 */

import type { ConsentState } from '@/lib/identity/consent'

/**
 * Bump when purposes or partners named in the banner change. A stored answer
 * with an older v is asked again. 2 = G3 (2026-10-23): Clarity named, US
 * analytics on by default with an opt-out.
 */
export const CONSENT_PURPOSES_VERSION = 2

/** Footer and settings icon dispatch this; the banner opens the second layer. */
export const OPEN_COOKIE_SETTINGS_EVENT = 'rr:open-cookie-settings'

/**
 * High-intent moment for the one extra US no-answer ask.
 * Dispatched from lib/tracking.ts trackEvent; the banner listens.
 */
export const CONTEXTUAL_CONSENT_ASK_EVENT = 'rr:contextual-consent-ask'

/** Valuation request or saved-search request. address_submit is the valuation address step. */
export const CONTEXTUAL_CONSENT_EVENT_NAMES = [
  'valuation_requested',
  'save_search',
  'address_submit',
] as const

export type ContextualConsentEventName = (typeof CONTEXTUAL_CONSENT_EVENT_NAMES)[number]

export function isContextualConsentEvent(name: string): name is ContextualConsentEventName {
  return (CONTEXTUAL_CONSENT_EVENT_NAMES as readonly string[]).includes(name)
}

export const MS_PER_DAY = 24 * 60 * 60 * 1000
/**
 * First-layer bar and the floating settings icon stay off these paths.
 * Cookie settings in the footer still opens the second layer, so the banner stays mounted.
 * Matches HideOnLP: /lp/*, /admin, /sign/*, /concept/*. G3 adds the pages that
 * explain or set the choices: /privacy, /cookies and /privacy-choices.
 */
const CONSENT_INFO_PAGES = ['/privacy', '/cookies', '/privacy-choices'] as const

export function consentFirstLayerSuppressed(pathname: string | null | undefined): boolean {
  if (!pathname) return false
  if (pathname.startsWith('/lp/')) return true
  if (pathname === '/admin' || pathname.startsWith('/admin/')) return true
  if (pathname.startsWith('/sign/')) return true
  if (pathname.startsWith('/concept/')) return true
  for (const page of CONSENT_INFO_PAGES) {
    if (pathname === page || pathname.startsWith(`${page}/`)) return true
  }
  return false
}

/** localStorage: last shown time and whether the contextual ask was used. */
export const CONSENT_PROMPT_STORAGE_KEY = 'rr_consent_prompt'
/** sessionStorage: this tab has already begun a consent-prompt session. */
export const CONSENT_PROMPT_SESSION_KEY = 'rr_consent_session'

export type ConsentPromptStored = (ConsentState & { v?: number }) | null

export type ShouldShowConsentPromptArgs = {
  stored: ConsentPromptStored
  lastShownAt: number | null
  newSession: boolean
  gpc: boolean
  now: number
}

export function isConsentDecline(
  stored: ConsentPromptStored,
): stored is ConsentState & { v?: number } {
  return stored !== null && stored.analytics === false && stored.marketing === false
}

export function isConsentAccept(
  stored: ConsentPromptStored,
): stored is ConsentState & { v?: number } {
  return stored !== null && (stored.analytics === true || stored.marketing === true)
}

/**
 * Whether the first-layer bar may show. The banner paints that bar as soon as
 * this returns true. There is no chip and no scroll gate.
 *
 * G3 (Counsel 2026-10-22): the bar has no X, so it stays until a button is
 * clicked. GPC: never. Any stored answer (accept or decline): only when its
 * purposes version is older than the current one; the 12-month cookie expiry
 * covers the rest, and a decline never re-prompts. No answer: next session,
 * at most once per 24h since it was last shown. A same-tab reload is not a new
 * session, so the daily cap does not hide an unanswered notice on refresh.
 * React Strict Mode sets the session key on the first effect pass; skipping the
 * cap when newSession is false keeps that second pass from hiding the bar.
 */
export function shouldShowConsentPrompt({
  stored,
  lastShownAt,
  newSession,
  gpc,
  now,
}: ShouldShowConsentPromptArgs): boolean {
  if (gpc) return false

  let eligible = false
  if (isConsentAccept(stored)) {
    eligible = (stored.v ?? 0) < CONSENT_PURPOSES_VERSION
  } else if (isConsentDecline(stored)) {
    eligible = false
  } else {
    eligible = true
  }

  if (!eligible) return false
  if (newSession && lastShownAt != null && now - lastShownAt < MS_PER_DAY) return false
  return true
}

/**
 * One extra first-layer ask, once per browser, for US visitors with no answer.
 * Ignores the once-a-day cap. Never under GPC, in restricted regions, or after an answer.
 */
export function shouldShowContextualConsentAsk(args: {
  stored: ConsentPromptStored
  gpc: boolean
  restricted: boolean
  contextualAskUsed: boolean
}): boolean {
  if (args.gpc || args.restricted || args.contextualAskUsed) return false
  if (args.stored !== null) return false
  return true
}

export type PromptBookkeeping = {
  lastShownAt: number | null
  contextualAskUsed: boolean
}

export function parsePromptBookkeeping(raw: string | null | undefined): PromptBookkeeping {
  if (!raw) return { lastShownAt: null, contextualAskUsed: false }
  try {
    const parsed = JSON.parse(raw) as { lastShownAt?: unknown; contextualAskUsed?: unknown }
    const lastShownAt =
      typeof parsed.lastShownAt === 'number' && Number.isFinite(parsed.lastShownAt)
        ? parsed.lastShownAt
        : null
    return { lastShownAt, contextualAskUsed: parsed.contextualAskUsed === true }
  } catch {
    return { lastShownAt: null, contextualAskUsed: false }
  }
}

export function serializePromptBookkeeping(state: PromptBookkeeping): string {
  return JSON.stringify({
    lastShownAt: state.lastShownAt,
    contextualAskUsed: state.contextualAskUsed,
  })
}

/**
 * Extra cookie JSON fields the identity parser ignores. gpcAnalytics is set
 * when a visitor whose browser sends GPC affirmatively turned analytics on in
 * Your privacy choices (G3); without it GPC keeps analytics off.
 */
export function parseConsentDecisionMeta(raw: string | null | undefined): {
  ts: number | null
  v: number | null
  gpcAnalytics: boolean
} {
  if (!raw) return { ts: null, v: null, gpcAnalytics: false }
  try {
    const parsed = JSON.parse(decodeURIComponent(raw)) as { ts?: unknown; v?: unknown; gpcAnalytics?: unknown }
    const ts = typeof parsed.ts === 'number' && Number.isFinite(parsed.ts) ? parsed.ts : null
    const v = typeof parsed.v === 'number' && Number.isFinite(parsed.v) ? parsed.v : null
    return { ts, v, gpcAnalytics: parsed.gpcAnalytics === true }
  } catch {
    return { ts: null, v: null, gpcAnalytics: false }
  }
}
